import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { faKey } from '@fortawesome/pro-light-svg-icons/faKey';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { ErrorAlert } from '../../../common/views/ErrorAlert/ErrorAlert';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useReviewerSignIn } from '../useReviewerSignIn';
import { AuthFieldError } from './AuthFieldError';
import { authErrorKind } from './authErrorKind';
import { AuthRateLimitText } from './AuthRateLimitText';
import { authV2Messages } from './authV2Messages';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';

const messages = defineMessages({
  link: { id: 'app.sml.auth.v2.password.link', defaultMessage: 'Sign in with a password' },
  passwordLabel: { id: 'app.sml.auth.v2.password.label', defaultMessage: 'Password' },
  submit: { id: 'app.sml.auth.v2.password.submit', defaultMessage: 'Sign in' },
  refused: { id: 'app.sml.auth.v2.password.refused', defaultMessage: 'The email or the password is not right.' },
  back: { id: 'app.sml.auth.v2.password.back', defaultMessage: 'Use an email code instead' },
  note: {
    id: 'app.sml.auth.v2.password.note',
    defaultMessage: 'Only for accounts that MUSIC24 gave a password. Everyone else signs in with the email code.',
  },
});

/**
 * The "Sign in with a password" button of the sign-in page (spec 2026-10-10 MCP apps §3.3). It sits
 * under the Apple and Google buttons, and only on the OAuth flow (`returnTo` is the consent page):
 * the ChatGPT and Claude directory reviewers cannot receive a mailed code. Until 2026-10-12 it was
 * a small link under the legal line, where the cookie bar covered it.
 */
const AuthPasswordSignInButtonComponent: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const intl = useIntl();
  return (
    <div className="au-sso single">
      <button type="button" className="au-sso-btn" onClick={onClick}>
        <FontAwesomeIcon icon={faKey} />
        {intl.formatMessage(messages.link)}
      </button>
    </div>
  );
};

export const AuthPasswordSignInButton = TypedMemo(AuthPasswordSignInButtonComponent);

/** The password form. It takes the place of the email form; `onBack` shows the email form again. */
const AuthPasswordSignInComponent: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const intl = useIntl();
  const form = useReviewerSignIn();

  const errorKind = authErrorKind(form.error);
  return (
    <form className="au-form" onSubmit={form.submit} aria-label={intl.formatMessage(messages.link)}>
      {errorKind === 'rateLimit' ? (
        <AuthFieldError id="sml-auth-password-rate-limit">
          <AuthRateLimitText error={form.error} />
        </AuthFieldError>
      ) : null}
      {errorKind === 'badCode' ? <AuthFieldError>{intl.formatMessage(messages.refused)}</AuthFieldError> : null}
      {form.error !== null && errorKind === 'other' ? <ErrorAlert error={form.error} /> : null}

      <div className="field">
        <label className="au-lbl" htmlFor="sml-auth-password-email">
          {intl.formatMessage(authV2Messages.emailLabel)}
        </label>
        <div className="au-in">
          <input
            id="sml-auth-password-email"
            type="email"
            autoComplete="username"
            disabled={form.isLoading}
            value={form.email}
            onChange={(e) => form.setEmail(e.target.value)}
            placeholder={intl.formatMessage(authV2Messages.emailPlaceholder)}
          />
        </div>
      </div>
      <div className="field">
        <label className="au-lbl" htmlFor="sml-auth-password">
          {intl.formatMessage(messages.passwordLabel)}
        </label>
        <div className="au-in">
          <input
            id="sml-auth-password"
            type="password"
            autoComplete="current-password"
            disabled={form.isLoading}
            value={form.password}
            onChange={(e) => form.setPassword(e.target.value)}
          />
        </div>
      </div>
      <SMLButton
        color="secondary"
        onClick={form.submit}
        disabled={form.isLoading || form.email.trim() === '' || form.password === ''}
        isLoading={form.isLoading}
        stretch
        omitCap
        size="lg"
        submit
      >
        {intl.formatMessage(messages.submit)}
        <FontAwesomeIcon icon={faChevronRight} />
      </SMLButton>
      <p className="au-small">{withBrandWord(intl.formatMessage(messages.note))}</p>
      <p className="au-small">
        <button type="button" className="btn btn-link p-0 align-baseline" onClick={onBack}>
          {intl.formatMessage(messages.back)}
        </button>
      </p>
    </form>
  );
};

export const AuthPasswordSignIn = TypedMemo(AuthPasswordSignInComponent);
